// packages/ui/src/CheckboxGroup.tsx

import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { CheckboxField } from './CheckboxField';

export interface CheckboxGroupOption {
  value: string;
  label: string;
  labelKey?: string;
  description?: string;
  descriptionKey?: string;
  disabled?: boolean;
}

export interface CheckboxGroupProps {
  label: string;
  labelKey?: string;
  options: CheckboxGroupOption[];
  value: string[]; // Array of selected values
  onChange: (newValues: string[]) => void;
  className?: string; // For the wrapper div
  labelClassName?: string;
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  label,
  labelKey,
  options,
  value,
  onChange,
  className,
  labelClassName,
}) => {
  const { t } = useTranslation();
  const generatedId = useId();

  const resolvedLabel = labelKey ? t(labelKey) : label;

  const handleCheckboxChange = (optionValue: string, isChecked: boolean) => {
    if (isChecked) {
      onChange([...value, optionValue]);
    } else {
      onChange(value.filter((v) => v !== optionValue));
    }
  };

  return (
    <div className={clsx('space-y-3', className)} role="group" aria-labelledby={generatedId}>
      <p
        id={generatedId}
        className={clsx(
          'block text-base font-semibold text-slate-800',
          '[.theme-liquid-glass_&]:text-white/90',
          labelClassName
        )}
      >
        {resolvedLabel}
      </p>
      <div className="space-y-2">
        {options.map((option) => (
          <CheckboxField
            key={option.value}
            id={`${generatedId}-${option.value}`}
            label={option.label}
            labelKey={option.labelKey}
            description={option.description}
            descriptionKey={option.descriptionKey}
            checked={value.includes(option.value)}
            onChange={(isChecked) => handleCheckboxChange(option.value, isChecked)}
            disabled={option.disabled}
          />
        ))}
      </div>
    </div>
  );
};

CheckboxGroup.displayName = 'CheckboxGroup';
